import { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { useSelector } from "react-redux";

import { errorHandeling } from "../../../errorHandeling";
import apiUrl from "../../../libs/baseUrl";
import SubmitObjectionPopup from "../../SubmitObjectionPopup/SubmitObjectionPopup";

import "./punchmentSectionForEmployee.scss";

// Thousand separators (drops trailing .00), e.g. "50000.00" -> "50,000".
const withCommas = (n) => {
  const num = Number(n);
  return Number.isNaN(num) ? String(n) : num.toLocaleString("en-US");
};

// The applied penalty's value/amount — shown next to the penalty type when it exists.
const penaltyValue = (method, pct, fixed, days) => {
  if (method === "salary_percentage_deduction" && pct != null && pct !== "") return `${withCommas(pct)}%`;
  if (method === "salary_fixed_deduction" && fixed != null && fixed !== "") return withCommas(fixed);
  if ((method === "temporary_suspension" || method === "partial_suspension") && days != null && days !== "")
    return `${withCommas(days)} يوم`;
  return "";
};

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString() : "—");

const caseNumber = (c) => {
  if (c.reference_code) return c.reference_code;
  if (c.final_sequence_number)
    return c.final_sequence_year
      ? `${c.final_sequence_number} / ${c.final_sequence_year}`
      : `${c.final_sequence_number}`;
  return `#${c.id}`;
};

// ---- objections ----
const OBJECTION_CASE_STATUSES = ["approved", "partially_approved"];
const OBJECTION_WINDOW_DAYS = 7;
const OBJECTION_STATUS = { pending: "قيد المراجعة", approved: "مقبول", rejected: "مرفوض" };

const withinObjectionWindow = (finalizedAt) => {
  if (!finalizedAt) return false;
  const deadline = new Date(finalizedAt);
  deadline.setDate(deadline.getDate() + OBJECTION_WINDOW_DAYS);
  return new Date() <= deadline;
};

// The employee may object to their OWN approved penalty, once, within the window.
const canObject = (c, mine) => {
  const objections = Array.isArray(mine.objections) ? mine.objections : [];
  return (
    mine.decision_status === "approved" &&
    OBJECTION_CASE_STATUSES.includes(c.current_status) &&
    withinObjectionWindow(c.finalized_at) &&
    objections.length === 0
  );
};

/**
 * The employee's own behavioral-penalty cases (self-view, «معلومات العقوبة» tab).
 * Uses the visibility-scoped /disciplinary-cases endpoint, so the backend returns only
 * cases the viewer is a subject of, with just their own row (no co-defendants). Newest
 * first, with basic filtering by date range and case number.
 */
function PunchmentSectionForEmplouyee() {
  const token = useSelector((state) => state.user.token);

  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // draft filter inputs (applied on "بحث")
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [number, setNumber] = useState("");
  const [applied, setApplied] = useState({});
  const [objectionTarget, setObjectionTarget] = useState(null); // caseEmployeeId being objected

  const fetchCases = useCallback(
    async (page, filters) => {
      if (!token) return;
      setLoading(true);
      setError(null);
      try {
        const params = { page };
        if (filters.from_date) params.from_date = filters.from_date;
        if (filters.to_date) params.to_date = filters.to_date;
        if (filters.number) params.number = filters.number;

        const response = await axios.get(`${apiUrl}/disciplinary-cases/mine`, {
          headers: { Authorization: `Bearer ${token}`, Accept: "application/json" },
          params,
        });

        const result = response.data?.data;
        setCases(result?.data ?? []);
        setCurrentPage(result?.current_page ?? 1);
        setTotalPages(result?.last_page ?? 1);
      } catch (err) {
        setCases([]);
        setError(errorHandeling(err));
      } finally {
        setLoading(false);
      }
    },
    [token]
  );

  useEffect(() => {
    fetchCases(1, {});
  }, [fetchCases]);

  const applyFilters = (e) => {
    e?.preventDefault();
    const f = {
      from_date: dateFrom,
      to_date: dateTo,
      number: number.trim(),
    };
    setApplied(f);
    fetchCases(1, f);
  };

  const clearFilters = () => {
    setDateFrom("");
    setDateTo("");
    setNumber("");
    setApplied({});
    fetchCases(1, {});
  };

  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) fetchCases(page, applied);
  };

  return (
    <div className="EvaluationForEmployeeShowPage_PunchmentSection">
      <span className="EvaluationForEmployeeShowPage_PunchmentSection_header" dir="rtl">
        معلومات العقوبة
      </span>

      {/* ---- filters: date range + number ---- */}
      <form className="empPenaltyFilter" onSubmit={applyFilters} dir="rtl">
        <div className="empPenaltyFilter__field">
          <label>من تاريخ</label>
          <input type="date" value={dateFrom} max={dateTo || undefined} onChange={(e) => setDateFrom(e.target.value)} />
        </div>
        <div className="empPenaltyFilter__field">
          <label>إلى تاريخ</label>
          <input type="date" value={dateTo} min={dateFrom || undefined} onChange={(e) => setDateTo(e.target.value)} />
        </div>
        <div className="empPenaltyFilter__field">
          <label>رقم العقوبة</label>
          <input
            type="text"
            value={number}
            placeholder="ابحث برقم العقوبة"
            onChange={(e) => setNumber(e.target.value)}
          />
        </div>
        <div className="empPenaltyFilter__actions">
          <button type="submit" className="empPenaltyFilter__btn">بحث</button>
          <button type="button" className="empPenaltyFilter__btn empPenaltyFilter__btn--ghost" onClick={clearFilters}>
            مسح
          </button>
        </div>
      </form>

      {loading ? (
        <span
          className="forLaterWork"
          style={{ textAlign: "center", padding: "1rem", backgroundColor: "white" }}
          dir="rtl"
        >
          يتم التحميل ...
        </span>
      ) : error ? (
        <span dir="rtl" style={{ color: "red", padding: ".5rem" }}>
          {typeof error === "string" ? error.replace(/^\*/, "") : "حدث خطأ أثناء جلب البيانات"}
        </span>
      ) : cases.length === 0 ? (
        <span className="EvaluationForEmployeeShowPage_PunchmentSection_noPunchment" dir="rtl">
          لا توجد عقوبات سلوكية
        </span>
      ) : (
        <>
          <table className="punshmentForEmpShow_table" dir="rtl">
            <thead>
              <tr className="punshmentForEmpShow_header_row_table">
                <th>الرقم</th>
                <th>تاريخ الواقعة</th>
                <th>المخالفة</th>
                <th>العقوبة</th>
                <th>الاعتراض</th>
              </tr>
            </thead>
            <tbody>
              {cases.map((c) => {
                const mine = c.employees?.[0] || {};
                const penaltyType =
                  mine.final_penalty_type_name_snapshot ||
                  mine.recommended_penalty_type_name_snapshot ||
                  "—";
                const val = penaltyValue(
                  mine.final_penalty_method,
                  mine.final_salary_percentage_value,
                  mine.final_salary_fixed_value,
                  mine.final_suspension_days
                );
                const objections = Array.isArray(mine.objections) ? mine.objections : [];
                const obj = objections[0];
                return (
                  <tr key={c.id}>
                    <td>{caseNumber(c)}</td>
                    <td>{fmtDate(c.incident_date)}</td>
                    <td>{mine.violation_type_name_snapshot || "—"}</td>
                    <td>{penaltyType}{val ? ` — ${val}` : ""}</td>
                    <td>
                      {obj ? (
                        <div className="empObjCell">
                          <span className={`empObjChip empObjChip--${obj.status}`}>
                            {OBJECTION_STATUS[obj.status] || obj.status}
                          </span>
                          {obj.status !== "pending" && obj.review_notes && (
                            <span className="empObjNote">{obj.review_notes}</span>
                          )}
                        </div>
                      ) : canObject(c, mine) ? (
                        <button
                          type="button"
                          className="empObjBtn"
                          onClick={() => setObjectionTarget(mine.id)}
                        >
                          قدّم اعتراض
                        </button>
                      ) : (
                        "—"
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {totalPages > 1 && (
            <div className="empPenaltyPager" dir="rtl">
              <button
                type="button"
                className="buttonNum_pagination"
                disabled={currentPage <= 1}
                onClick={() => goToPage(currentPage - 1)}
              >
                السابق
              </button>
              <span className="empPenaltyPager__label">
                صفحة {currentPage} من {totalPages}
              </span>
              <button
                type="button"
                className="buttonNum_pagination"
                disabled={currentPage >= totalPages}
                onClick={() => goToPage(currentPage + 1)}
              >
                التالي
              </button>
            </div>
          )}
        </>
      )}

      {objectionTarget &&
        createPortal(
          <SubmitObjectionPopup
            caseEmployeeId={objectionTarget}
            onClose={() => setObjectionTarget(null)}
            onSuccess={() => fetchCases(currentPage, applied)}
          />,
          document.body
        )}
    </div>
  );
}

export default PunchmentSectionForEmplouyee;
